/*
Copyright 2026 Element Creations Ltd.

SPDX-License-Identifier: AGPL-3.0-only OR LicenseRef-Element-Commercial
Please see LICENSE in the repository root for full details.
*/

import { type FC, type ReactNode, useState } from "react";
import { useTranslation } from "react-i18next";

import { FieldRow, InputField } from "../input/Input";
import {
  type Setting,
  useSetting,
  echoCancellationSetting,
  noiseSuppressionSetting,
  autoGainControlSetting,
  type VideoCodec,
} from "./settings";
import styles from "./SettingsModal.module.css";
import { Slider } from "../Slider";

/**
 * Per-track media quality controls (resolution, framerate, bitrate, codec).
 * Moved out of the developer settings tab so users can tune sharpness
 * themselves: the toggle selects between automatic capture (native/"source"
 * mode) and fully manual encoding parameters.
 */
export const MediaQualitySettings: FC<{
  id: string;
  header: string;
  toggleLabel: string;
  description: string;
  toggleSetting: Setting<boolean>;
  resolutionSetting: Setting<string>;
  framerateSetting: Setting<number>;
  bitrateSetting: Setting<number>;
  codecSetting: Setting<VideoCodec>;
  resolutionOptions: { value: string; label: string }[];
  bitrateRange: { min: number; max: number; step: number };
}> = ({
  id,
  header,
  toggleLabel,
  description,
  toggleSetting,
  resolutionSetting,
  framerateSetting,
  bitrateSetting,
  codecSetting,
  resolutionOptions,
  bitrateRange,
}) => {
  const { t } = useTranslation();
  const [advancedEnabled, setAdvancedEnabled] = useSetting(toggleSetting);
  const [resolution, setResolution] = useSetting(resolutionSetting);
  const [framerate, setFramerate] = useSetting(framerateSetting);
  const [framerateRaw, setFramerateRaw] = useState(framerate);
  const [bitrate, setBitrate] = useSetting(bitrateSetting);
  const [bitrateRaw, setBitrateRaw] = useState(bitrate);
  const [codec, setCodec] = useSetting(codecSetting);

  return (
    <>
      <h4>{header}</h4>
      <FieldRow>
        <InputField
          id={`${id}Toggle`}
          label={toggleLabel}
          description={description}
          type="checkbox"
          checked={advancedEnabled}
          onChange={(e): void => setAdvancedEnabled(e.target.checked)}
        />
      </FieldRow>
      {advancedEnabled && (
        <>
          <div className={styles.volumeSlider}>
            <label htmlFor={`${id}Resolution`}>
              {t("settings.resolution_label", "Resolution")}
            </label>
            <select
              id={`${id}Resolution`}
              value={resolution}
              onChange={(e): void => setResolution(e.target.value)}
            >
              {resolutionOptions.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>
          <div className={styles.volumeSlider}>
            <label>
              {t("settings.framerate_label", "Framerate")}
              {": "}
              <span className={styles.settingValue}>{framerateRaw} fps</span>
            </label>
            <Slider
              label={t("settings.framerate_label", "Framerate")}
              value={framerateRaw}
              onValueChange={setFramerateRaw}
              onValueCommit={setFramerate}
              min={5}
              max={60}
              step={5}
              tooltipFormatter={(v): string => `${v} fps`}
            />
          </div>
          <div className={styles.volumeSlider}>
            <label>
              {t("settings.bitrate_label", "Bitrate")}
              {": "}
              <span className={styles.settingValue}>
                {(bitrateRaw / 1_000_000).toFixed(1)} Mbps
              </span>
            </label>
            <Slider
              label={t("settings.bitrate_label", "Bitrate")}
              value={bitrateRaw}
              onValueChange={setBitrateRaw}
              onValueCommit={setBitrate}
              min={bitrateRange.min}
              max={bitrateRange.max}
              step={bitrateRange.step}
              tooltipFormatter={(v): string =>
                `${(v / 1_000_000).toFixed(1)} Mbps`
              }
            />
          </div>
          <div className={styles.volumeSlider}>
            <label htmlFor={`${id}Codec`}>
              {t("settings.codec_label", "Codec")}
            </label>
            <select
              id={`${id}Codec`}
              value={codec}
              onChange={(e): void => setCodec(e.target.value as VideoCodec)}
            >
              <option value="vp8">VP8</option>
              <option value="vp9">VP9</option>
              <option value="h264">H.264</option>
              <option value="av1">AV1</option>
            </select>
          </div>
        </>
      )}
    </>
  );
};

/**
 * Browser audio processing toggles, shown on the audio settings tab.
 */
export const AudioProcessingSettings: FC = (): ReactNode => {
  const { t } = useTranslation();
  const [echoCancellation, setEchoCancellation] = useSetting(
    echoCancellationSetting,
  );
  const [noiseSuppression, setNoiseSuppression] = useSetting(
    noiseSuppressionSetting,
  );
  const [autoGainControl, setAutoGainControl] = useSetting(
    autoGainControlSetting,
  );

  return (
    <>
      <h4>{t("settings.audio_processing_header", "Audio processing")}</h4>
      <p>
        {t(
          "settings.audio_processing_description",
          "Changes apply on next call join.",
        )}
      </p>
      <FieldRow>
        <InputField
          id="echoCancellation"
          label={t("settings.echo_cancellation_label", "Echo cancellation")}
          type="checkbox"
          checked={echoCancellation}
          onChange={(e): void => setEchoCancellation(e.target.checked)}
        />
      </FieldRow>
      <FieldRow>
        <InputField
          id="noiseSuppression"
          label={t("settings.noise_suppression_label", "Noise suppression")}
          type="checkbox"
          checked={noiseSuppression}
          onChange={(e): void => setNoiseSuppression(e.target.checked)}
        />
      </FieldRow>
      <FieldRow>
        <InputField
          id="autoGainControl"
          label={t(
            "settings.auto_gain_control_label",
            "Automatic gain control",
          )}
          type="checkbox"
          checked={autoGainControl}
          onChange={(e): void => setAutoGainControl(e.target.checked)}
        />
      </FieldRow>
    </>
  );
};
